import { ContentCopy, GridViewOutlined, WhatsApp } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { showSuccessToast } from "../../../../components/common/ShowToast";
import { CalendarIcon, PhoneIcon } from "../../../../components/icons";
import { ArrowsExpandIcon } from "../../../../components/icons/ArrowsExpandIcon";
import { PersonIcon } from "../../../../components/icons/PersonIcon";
import { BookingData } from "../../types/view-booking";

interface Props {
  open: boolean;
  onClose: () => void;
  bookingData?: BookingData | null;
}

export default function RequestClientReviewModal({
  open,
  onClose,
  bookingData,
}: Props) {
  const [includeClientName, setIncludeClientName] = useState(true);

  const clientName = bookingData?.organiser?.name || "Client";
  const phone = bookingData?.organiser?.phone || "";
  const service = bookingData?.service || "Memorial Service";
  const reviewLink = `review.company.com/client-review/${bookingData?.id ?? "1234"}`;

  const rawDate = bookingData?.streams?.[0]?.date;
  const serviceDate = rawDate
    ? (() => {
        const [day, month, year] = rawDate.split("-");
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      })()
    : "N/A";

  const baseMessage = useCallback(
    (name: string) =>
      `Hello ${name},\nThank you for choosing us. We hope everything went well.\nRegarding your ${service} on ${serviceDate} —\nYour feedback helps us improve our service and better support our clients.\nPlease take a few seconds to rate your experience:`,
    [service, serviceDate],
  );

  useEffect(() => {
    setMessage(baseMessage(clientName));
  }, [baseMessage, clientName]);
  const [message, setMessage] = useState("");

  const whatsappMessage = `${message}\n${reviewLink}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(reviewLink);
    showSuccessToast("Link copied!");
  };

  const handleShareWhatsApp = () => {
    const encoded = encodeURIComponent(whatsappMessage);
    const phoneClean = phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phoneClean}?text=${encoded}`, "_blank");
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIncludeClientName(checked);
    setMessage(baseMessage(checked ? clientName : "there"));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 5, maxWidth: 480 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 24, pb: 1.5 }}>
        Request client review
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ bgcolor: "#f0f4f8", borderRadius: 2.5, p: 1.5, mb: 2 }}>
          <Typography
            fontWeight={600}
            sx={{ mb: 1.5, display: "block", fontSize: 16 }}
          >
            Client details
          </Typography>
          <Grid container spacing={1.5}>
            {[
              {
                icon: <PersonIcon fontSize="small" />,
                label: "Name",
                value: clientName,
              },
              {
                icon: <PhoneIcon fontSize="small" />,
                label: "Phone",
                value: phone || "N/A",
              },
              {
                icon: <GridViewOutlined fontSize="small" />,
                label: "Service",
                value: service,
              },
              {
                icon: <CalendarIcon fontSize="small" />,
                label: "Date",
                value: serviceDate,
              },
            ].map(({ icon, label, value }) => (
              <Grid size={{ xs: 6 }} key={label}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 2,
                      bgcolor: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {value}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>

        <TextField
          fullWidth
          multiline
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              fontSize: 13,
              lineHeight: 1.7,
            },
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Switch
            checked={includeClientName}
            onChange={handleToggle}
            size="medium"
            sx={{
              "& .MuiSwitch-thumb": { bgcolor: "white" },
              "& .Mui-checked + .MuiSwitch-track": {
                bgcolor: "#724E41 !important",
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Include client name
          </Typography>
        </Stack>
        <Box sx={{ bgcolor: "#f0f4f8", borderRadius: 2.5, p: 1.5, mb: 2 }}>
          <Typography
            fontWeight={600}
            sx={{ mb: 1, display: "block", fontSize: 16 }}
          >
            Review link
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                borderRadius: 4,
                px: 1.5,
                py: 1,
                fontSize: 13,
                color: "text.secondary",
                overflow: "hidden",
                bgcolor: "#ffffff",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {reviewLink}
            </Box>
            <Tooltip title="Copy link">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={handleCopy}
              >
                <ContentCopy sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>

            <Tooltip title="Open link">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => window.open(`https://${reviewLink}`, "_blank")}
              >
                <ArrowsExpandIcon sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          </Stack>
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleShareWhatsApp}
            startIcon={<WhatsApp />}
            sx={{
              bgcolor: "#724E41",
              textTransform: "none",
              borderRadius: 2,
              py: 1,
              fontSize: 14,
              fontWeight: 600,
              "&:hover": { bgcolor: "#5d3e33" },
            }}
          >
            Share via WhatsApp
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
